// G-AIRMET (Graphical Airmen's Meteorological Information) layer family.
//
// Light weather hazards: IFR, mountain obscuration, turbulence, icing, wind.
// One GeoJSON source drives two layers: a low-opacity hazard-coded fill and
// a dashed hazard-coded border line. Both layers are mounted with
// visibility: 'none' (lazy toggle via the wx-hazards bar).
//
// Extracted from addOverlays.ts as HI-1 batch 7 round 2 (2026-05-24).
// Pure mechanical move — source spec, paint expressions, layer ids, and
// layer order are byte-identical to the pre-extraction form.
//
// applyState reads visibility from useUiStore so a basemap setStyle()
// rebuild restores the user's current toggle state transparently.

import type { Map as MlMap } from 'maplibre-gl';
import { EMPTY_FC } from '../MapView';
import { registerLayerFamily } from '../layerVisibilityRegistry';
import { useLayerVisibilityStore } from '../../stores/layerVisibilityStore';
import { isOverlayLoaded, markOverlayLoaded, refreshOverlay } from '../overlayRuntime';
import { OVERLAY_BY_ID } from '../overlaySpecs';

export function addAirmetLayers(map: MlMap): void {
  if (!map.getSource('airmet')) {
    map.addSource('airmet', { type: 'geojson', data: EMPTY_FC });
    map.addLayer({
      id: 'airmet-fill',
      type: 'fill',
      source: 'airmet',
      layout: { visibility: 'none' },
      paint: {
        'fill-color': [
          'match', ['get', 'hazard'],
          'IFR', '#3b82f6',
          'MT_OBSC', '#a78bfa',
          'TURB', '#f59e0b',
          'ICE', '#06b6d4',
          'WIND', '#10b981',
          /* default */ '#94a3b8',
        ],
        'fill-opacity': 0.18,
      },
    });
    map.addLayer({
      id: 'airmet-line',
      type: 'line',
      source: 'airmet',
      layout: { visibility: 'none' },
      paint: {
        'line-color': [
          'match', ['get', 'hazard'],
          'IFR', '#3b82f6',
          'MT_OBSC', '#a78bfa',
          'TURB', '#f59e0b',
          'ICE', '#06b6d4',
          'WIND', '#10b981',
          /* default */ '#94a3b8',
        ],
        'line-width': 1.5,
        'line-dasharray': [2, 2],
      },
    });
  }
}

registerLayerFamily({
  id: 'airmet',
  label: 'G-AIRMET',
  sourceId: 'airmet',
  layerIds: ['airmet-fill', 'airmet-line'],
  mount: (map) => addAirmetLayers(map),
  visibilitySelector: (l) => l.wxhazards && l.airmet,
  applyState: (map) => {
    if (!map.getLayer('airmet-fill')) return;
    const layers = useLayerVisibilityStore.getState().layers;
    const show = layers.wxhazards && layers.airmet;
    const v = show ? 'visible' : 'none';
    map.setLayoutProperty('airmet-fill', 'visibility', v);
    map.setLayoutProperty('airmet-line', 'visibility', v);
  },
  onToggleOn: (map) => {
    const spec = OVERLAY_BY_ID.get('airmet');
    if (!spec || isOverlayLoaded('airmet')) return;
    markOverlayLoaded('airmet');
    return refreshOverlay(map, spec);
  },
});
